import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { format, isAfter, parseISO, startOfWeek } from 'date-fns';
import { ru, enGB, fr } from 'date-fns/locale';
import {
  Activity,
  CalendarDays,
  Check,
  Clock3,
  Edit3,
  Filter,
  NotebookText,
  Plus,
  RefreshCcw,
  Search,
  Sparkles,
  Star,
  Trash2,
  X,
  Zap,
} from 'lucide-react';

import { equipment, trainings } from '../../lib/api';
import { markTrainingDataUpdated } from '../../lib/dataFreshness';
import { Button, Field, Input, Select, Textarea } from '../../components/ui-kit';
import { RateTrainingModal } from '../../components/feedback/RateTrainingModal';
import { useFeatureFlag } from '../../context/AuthContext';

const LOCALES = { ru, en: enGB, fr };
const TRAINING_TYPES = ['tennis', 'strength', 'cardio', 'general'];
const RATED_STORAGE_KEY = 'td.rated.training_ids';

const emptyForm = () => ({
  date: format(new Date(), 'yyyy-MM-dd'),
  type: 'tennis',
  duration: 60,
  exercises: '',
  notes: '',
  coach_comment: '',
  equipment_ids: [],
});

const safeDate = (value) => {
  if (!value) return null;
  const parsed = typeof value === 'string' ? parseISO(value) : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatTrainingDate = (value, locale) => {
  const parsed = safeDate(value);
  return parsed ? format(parsed, 'dd MMM yyyy', { locale }) : '—';
};

const toDateInputValue = (value) => {
  const parsed = safeDate(value);
  return parsed ? format(parsed, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
};

const parseFocusAreas = (training) =>
  String(training.exercises || '')
    .split(/[,;\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 4);

const getIntensity = (duration) => {
  const minutes = Number(duration) || 0;
  if (minutes >= 90) return 'high';
  if (minutes >= 45) return 'medium';
  return 'low';
};

const loadCachedRated = () => {
  try {
    const raw = window.localStorage.getItem(RATED_STORAGE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const persistRated = (set) => {
  try {
    window.localStorage.setItem(RATED_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* storage quota; ignore */
  }
};

const TypeBadge = ({ type, t }) => (
  <span
    className="inline-flex items-center rounded-full bg-zinc-950 px-3 py-1 text-xs font-black text-lime-300"
    data-testid={type ? undefined : 'training-type-empty'}
  >
    {t(`training.types.${type}`, type || t('common.type'))}
  </span>
);

const IntensityBadge = ({ duration, t }) => {
  const intensity = getIntensity(duration);
  const tones = {
    high: 'bg-red-50 text-red-700 ring-red-100',
    medium: 'bg-amber-50 text-amber-700 ring-amber-100',
    low: 'bg-lime-50 text-lime-700 ring-lime-100',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-black ring-1 ${tones[intensity]}`}>
      {t(`training.intensity.${intensity}`, intensity)}
    </span>
  );
};

const equipmentLabel = (item, t) =>
  item?.category
    ? `${item.name} (${t(`equipment.categories.${item.category}`, item.category)})`
    : item?.name;

const selectedEquipmentIdsFromEvent = (event) =>
  Array.from(event.target.selectedOptions).map((option) => option.value);

const LinkedEquipmentList = ({ equipmentIds = [], equipmentById, t }) => {
  if (!Array.isArray(equipmentIds) || equipmentIds.length === 0) return null;
  const labels = equipmentIds.map((id) => equipmentById.get(id)?.name || id);

  return (
    <div className="mt-4 rounded-lg border border-zinc-200 bg-white p-3">
      <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
        {t('equipment.equipmentUsed')}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {labels.map((label) => (
          <span key={label} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-bold text-zinc-700 ring-1 ring-zinc-200">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const EquipmentSelector = ({ equipmentList, selectedIds, onChange, t }) => (
  <Field
    label={t('equipment.linkedEquipment')}
    hint={equipmentList.length > 0 ? t('equipment.selectEquipment') : t('equipment.addEquipmentFirst')}
  >
    {equipmentList.length > 0 ? (
      <Select
        multiple
        className="min-h-32"
        value={selectedIds || []}
        onChange={(event) => onChange(selectedEquipmentIdsFromEvent(event))}
      >
        {equipmentList.map((item) => (
          <option key={item.id} value={item.id}>
            {equipmentLabel(item, t)}
          </option>
        ))}
      </Select>
    ) : (
      <p className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-3 text-sm text-zinc-500">
        {t('equipment.noEquipmentAddedYet')} {t('equipment.addEquipmentFirst')}.
      </p>
    )}
  </Field>
);

const RateAction = ({ training, hasRated, onOpen, t }) =>
  hasRated ? (
    <span
      className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-lime-50 px-3 text-sm font-bold text-lime-700"
      data-testid={`training-rated-${training.id}`}
    >
      <Check size={15} strokeWidth={1.8} /> {t('feedback.youRated')}
    </span>
  ) : (
    <Button
      variant="ghost"
      size="sm"
      leftIcon={<Star size={15} strokeWidth={1.8} />}
      onClick={() => onOpen(training)}
      data-testid={`training-rate-${training.id}`}
    >
      {t('feedback.rateNow')}
    </Button>
  );

const StatTile = ({ icon, label, value, detail }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-zinc-500">{label}</p>
        <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-lime-50 text-lime-700 ring-1 ring-lime-100">
        {icon}
      </div>
    </div>
    {detail ? <p className="mt-3 text-sm text-zinc-500">{detail}</p> : null}
  </div>
);

const TrainingSkeleton = () => (
  <div className="grid gap-4 md:grid-cols-2">
    {[0, 1, 2, 3].map((item) => (
      <div key={item} className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
        <div className="h-4 w-24 animate-pulse rounded bg-zinc-200" />
        <div className="mt-4 h-7 w-40 animate-pulse rounded bg-zinc-200" />
        <div className="mt-5 h-20 animate-pulse rounded bg-zinc-100" />
      </div>
    ))}
  </div>
);

const EmptyTrainingState = ({ onAdd, t }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-white p-8 text-center" data-testid="trainings-empty">
    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      <Activity size={26} strokeWidth={1.8} />
    </div>
    <h2 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
      {t('training.noTrainings')}
    </h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
      {t('training.emptyBody', 'Log your first session to start building a useful training history.')}
    </p>
    <button
      type="button"
      onClick={onAdd}
      className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 hover:bg-lime-200"
    >
      <Plus size={16} strokeWidth={1.8} />
      {t('training.addTraining', 'Add training')}
    </button>
  </div>
);

const ErrorState = ({ onRetry, t }) => (
  <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-amber-950">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-black">{t('training.loadError')}</h2>
        <p className="mt-1 text-sm text-amber-800">
          {t('training.loadErrorBody', 'Training history could not be refreshed.')}
        </p>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 text-sm font-black text-amber-950"
      >
        <RefreshCcw size={16} strokeWidth={1.8} />
        {t('common.retry', 'Retry')}
      </button>
    </div>
  </div>
);

const TrainingCard = ({
  training,
  locale,
  ratedIds,
  feedbackEnabled,
  onRate,
  onEdit,
  onDelete,
  equipmentById,
  t,
}) => {
  const focusAreas = parseFocusAreas(training);
  return (
    <article
      className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`training-row-${training.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {formatTrainingDate(training.date, locale)}
          </p>
          <h3 className="mt-2 text-xl font-black tracking-normal text-zinc-950">
            {t(`training.types.${training.type}`, training.type)}
          </h3>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span data-testid={`training-type-${training.id}`}>
            <TypeBadge type={training.type} t={t} />
          </span>
          <IntensityBadge duration={training.duration} t={t} />
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg bg-zinc-50 p-3">
          <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            <Clock3 size={14} strokeWidth={1.8} />
            {t('training.duration')}
          </p>
          <p className="mt-1 text-lg font-black text-zinc-950">
            {training.duration} {t('common.min')}
          </p>
        </div>
        <div className="rounded-lg bg-zinc-50 p-3">
          <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
            <Star size={14} strokeWidth={1.8} />
            {t('training.rating', 'Rating')}
          </p>
          <p className="mt-1 text-lg font-black text-zinc-950">
            {ratedIds.has(training.id) ? t('feedback.youRated') : '—'}
          </p>
        </div>
      </div>

      {focusAreas.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {focusAreas.map((area) => (
            <span key={area} className="rounded-full bg-lime-50 px-3 py-1 text-xs font-bold text-lime-800 ring-1 ring-lime-100">
              {area}
            </span>
          ))}
        </div>
      ) : null}

      <LinkedEquipmentList
        equipmentIds={training.equipment_ids}
        equipmentById={equipmentById}
        t={t}
      />

      <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
        <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.12em] text-zinc-500">
          <NotebookText size={14} strokeWidth={1.8} />
          {t('training.notes')}
        </p>
        <p className="mt-2 line-clamp-3 text-sm leading-6 text-zinc-600">
          {training.notes || '—'}
        </p>
      </div>

      {training.coach_comment ? (
        <div className="mt-3 rounded-lg border border-cyan-100 bg-cyan-50 p-3 text-sm text-cyan-900">
          <strong>{t('training.coachComment')}:</strong> {training.coach_comment}
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onEdit(training)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-black text-zinc-950 hover:bg-zinc-50"
          >
            <Edit3 size={15} strokeWidth={1.8} />
            {t('common.edit', 'Edit')}
          </button>
          <button
            type="button"
            onClick={() => onDelete(training)}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-red-100 bg-red-50 px-3 text-sm font-black text-red-700 hover:bg-red-100"
          >
            <Trash2 size={15} strokeWidth={1.8} />
            {t('common.delete', 'Delete')}
          </button>
        </div>
        {feedbackEnabled ? (
          <RateAction
            training={training}
            hasRated={ratedIds.has(training.id)}
            onOpen={onRate}
            t={t}
          />
        ) : null}
      </div>
    </article>
  );
};

const TrainingForm = ({
  mode,
  formData,
  setFormData,
  equipmentList,
  onSubmit,
  onClose,
  submitting,
  t,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4">
    <div className="w-full max-w-2xl overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 border-b border-zinc-200 bg-zinc-950 p-5 text-white">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
            {t('training.formKicker', 'Training form')}
          </p>
          <h2 className="mt-1 text-2xl font-black tracking-normal text-white">
            {mode === 'edit' ? t('training.editTraining', 'Edit training') : t('training.addTraining', 'Add training')}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white"
          aria-label={t('common.close', 'Close')}
        >
          <X size={20} strokeWidth={1.8} />
        </button>
      </div>
      <form onSubmit={onSubmit} className="grid max-h-[78vh] gap-4 overflow-y-auto border-0 bg-white p-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('training.date')} required>
            <Input
              type="date"
              value={formData.date}
              onChange={(event) => setFormData({ ...formData, date: event.target.value })}
              required
            />
          </Field>
          <Field label={t('training.type')} required>
            <Select
              value={formData.type}
              onChange={(event) => setFormData({ ...formData, type: event.target.value })}
              required
            >
              {TRAINING_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`training.types.${type}`, type)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label={t('training.duration')} required hint={t('training.durationHint', 'Minutes on court or in session.')}>
          <Input
            type="number"
            min="1"
            max="480"
            inputMode="numeric"
            value={formData.duration}
            onChange={(event) => setFormData({ ...formData, duration: event.target.value })}
            required
          />
        </Field>

        <Field label={t('training.exercises')} hint={t('training.exercisesHint', 'Separate focus areas with commas.')}>
          <Input
            type="text"
            value={formData.exercises}
            onChange={(event) => setFormData({ ...formData, exercises: event.target.value })}
            placeholder={t('training.exercisesPlaceholder', 'Serve, footwork, backhand')}
          />
        </Field>

        <Field label={t('training.notes')}>
          <Textarea
            rows={4}
            value={formData.notes}
            onChange={(event) => setFormData({ ...formData, notes: event.target.value })}
          />
        </Field>

        <Field label={t('training.coachComment')}>
          <Textarea
            rows={3}
            value={formData.coach_comment}
            onChange={(event) => setFormData({ ...formData, coach_comment: event.target.value })}
          />
        </Field>

        <EquipmentSelector
          equipmentList={equipmentList}
          selectedIds={formData.equipment_ids}
          onChange={(equipmentIds) => setFormData({ ...formData, equipment_ids: equipmentIds })}
          t={t}
        />

        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" size="lg" loading={submitting}>
            {mode === 'edit' ? t('common.save', 'Save') : t('training.addTraining', 'Add training')}
          </Button>
        </div>
      </form>
    </div>
  </div>
);

export const TrainingsPage = () => {
  const [trainingsList, setTrainingsList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [activeRateTraining, setActiveRateTraining] = useState(null);
  const [ratedIds, setRatedIds] = useState(loadCachedRated);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formMode, setFormMode] = useState(null);
  const [activeTraining, setActiveTraining] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const { t, i18n } = useTranslation();
  const feedbackEnabled = useFeatureFlag('coach_feedback');

  const fetchTrainings = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [trainingsResult, equipmentResult] = await Promise.allSettled([
        trainings.getAll(),
        equipment.getAll(),
      ]);
      if (trainingsResult.status === 'rejected') throw trainingsResult.reason;
      setTrainingsList(trainingsResult.value.data);
      setEquipmentList(
        equipmentResult.status === 'fulfilled' && Array.isArray(equipmentResult.value.data)
          ? equipmentResult.value.data
          : []
      );
    } catch (error) {
      toast.error(t('training.loadError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTrainings();
  }, [fetchTrainings]);

  const markRated = useCallback((trainingId) => {
    setRatedIds((prev) => {
      const next = new Set(prev);
      next.add(trainingId);
      persistRated(next);
      return next;
    });
  }, []);

  const locale = useMemo(
    () => LOCALES[i18n.language] || enGB,
    [i18n.language],
  );
  const equipmentById = useMemo(
    () => new Map(equipmentList.map((item) => [item.id, item])),
    [equipmentList],
  );

  const stats = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekly = trainingsList.filter((training) => {
      const date = safeDate(training.date);
      return date && isAfter(date, weekStart);
    });
    const totalMinutes = weekly.reduce((sum, training) => sum + (Number(training.duration) || 0), 0);
    const sortedDays = [...new Set(trainingsList.map((training) => toDateInputValue(training.date)))].sort().reverse();
    let streak = 0;
    let cursor = new Date();
    for (const day of sortedDays) {
      const expected = format(cursor, 'yyyy-MM-dd');
      if (day === expected) {
        streak += 1;
        cursor.setDate(cursor.getDate() - 1);
      } else if (streak > 0) {
        break;
      }
    }
    return {
      weeklySessions: weekly.length,
      weeklyHours: (totalMinutes / 60).toFixed(1),
      avgRating: ratedIds.size > 0 ? `${ratedIds.size}/${trainingsList.length || ratedIds.size}` : '—',
      streak,
    };
  }, [trainingsList, ratedIds]);

  const typeOptions = useMemo(() => {
    const types = new Set(TRAINING_TYPES);
    trainingsList.forEach((training) => {
      if (training.type) types.add(training.type);
    });
    return ['all', ...types];
  }, [trainingsList]);

  const filteredTrainings = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return trainingsList.filter((training) => {
      const matchesType = typeFilter === 'all' || training.type === typeFilter;
      const haystack = [
        training.type,
        training.exercises,
        training.notes,
        training.coach_comment,
        formatTrainingDate(training.date, locale),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesQuery = !normalizedQuery || haystack.includes(normalizedQuery);
      return matchesType && matchesQuery;
    });
  }, [trainingsList, typeFilter, query, locale]);

  const openCreate = () => {
    setActiveTraining(null);
    setFormData(emptyForm());
    setFormMode('create');
  };

  const openEdit = (training) => {
    setActiveTraining(training);
    setFormData({
      date: toDateInputValue(training.date),
      type: training.type || 'tennis',
      duration: training.duration || 60,
      exercises: training.exercises || '',
      notes: training.notes || '',
      coach_comment: training.coach_comment || '',
      equipment_ids: Array.isArray(training.equipment_ids) ? training.equipment_ids : [],
    });
    setFormMode('edit');
  };

  const closeForm = () => {
    setFormMode(null);
    setActiveTraining(null);
    setFormData(emptyForm());
  };

  const buildPayload = () => ({
    date: new Date(formData.date).toISOString(),
    type: formData.type,
    duration: Number(formData.duration),
    exercises: formData.exercises.trim() || null,
    notes: formData.notes.trim() || null,
    coach_comment: formData.coach_comment.trim() || null,
    equipment_ids: Array.isArray(formData.equipment_ids) ? formData.equipment_ids : [],
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (formMode === 'edit' && activeTraining?.id) {
        await trainings.update(activeTraining.id, payload);
        toast.success(t('training.updateSuccess', 'Training updated.'));
      } else {
        await trainings.create(payload);
        toast.success(t('training.createSuccess', 'Training added.'));
      }
      markTrainingDataUpdated();
      closeForm();
      fetchTrainings();
    } catch (error) {
      toast.error(formMode === 'edit' ? t('training.updateError', 'Could not update training.') : t('training.createError', 'Could not add training.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (training) => {
    const confirmed = window.confirm(t('training.deleteConfirm', 'Delete this training session?'));
    if (!confirmed) return;
    try {
      await trainings.delete(training.id);
      toast.success(t('training.deleteSuccess', 'Training deleted.'));
      markTrainingDataUpdated();
      fetchTrainings();
    } catch (error) {
      toast.error(t('training.deleteError', 'Could not delete training.'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-zinc-100 p-4 lg:p-8" data-testid="trainings-loading">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 h-52 animate-pulse rounded-lg bg-zinc-200" />
          <TrainingSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 px-4 py-5 text-zinc-950 lg:px-8 lg:py-8" data-testid="trainings-page">
      <div className="mx-auto max-w-7xl">
        <section className="relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 p-5 text-white shadow-xl md:p-7">
          <div
            className="absolute inset-0 bg-[linear-gradient(90deg,rgba(183,255,42,0.18)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.09)_1px,transparent_1px)] bg-[size:72px_72px] opacity-45"
            aria-hidden="true"
          />
          <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
                <Sparkles size={14} strokeWidth={1.8} />
                {t('common.athleteDiary', 'Athlete diary')}
              </div>
              <h1 className="mt-5 text-4xl font-black tracking-normal text-white md:text-5xl">
                {t('training.title')}
              </h1>
              <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-200">
                {t('training.subtitle')}
              </p>
            </div>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 hover:bg-lime-200"
            >
              <Plus size={18} strokeWidth={1.8} />
              {t('training.addTraining', 'Add training')}
            </button>
          </div>
        </section>

        {loadFailed ? <div className="mt-5"><ErrorState onRetry={fetchTrainings} t={t} /></div> : null}

        <section className="mt-5 grid gap-4 md:grid-cols-4">
          <StatTile
            icon={<Activity size={22} strokeWidth={1.8} />}
            label={t('training.weeklySessions', 'This week')}
            value={stats.weeklySessions}
            detail={t('training.sessions', 'sessions')}
          />
          <StatTile
            icon={<Clock3 size={22} strokeWidth={1.8} />}
            label={t('training.weeklyHours', 'Training hours')}
            value={stats.weeklyHours}
            detail={t('training.hoursThisWeek', 'hours this week')}
          />
          <StatTile
            icon={<Star size={22} strokeWidth={1.8} />}
            label={t('training.averageRating', 'Rated sessions')}
            value={stats.avgRating}
            detail={t('training.ratingDetail', 'from local feedback')}
          />
          <StatTile
            icon={<Zap size={22} strokeWidth={1.8} />}
            label={t('training.streak', 'Streak')}
            value={stats.streak}
            detail={t('training.days', 'days')}
          />
        </section>

        <section className="mt-5 grid gap-4 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm lg:grid-cols-[minmax(0,1fr)_14rem]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} strokeWidth={1.8} />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('training.searchPlaceholder', 'Search type, focus, notes...')}
              className="min-h-11 w-full rounded-lg border border-zinc-200 bg-zinc-50 py-2 pl-10 pr-3 text-sm text-zinc-950 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-200/50"
            />
          </div>
          <label className="relative block">
            <Filter className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} strokeWidth={1.8} />
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="min-h-11 w-full appearance-none rounded-lg border border-zinc-200 bg-zinc-50 py-2 pl-10 pr-3 text-sm font-bold text-zinc-950 outline-none transition focus:border-lime-400 focus:bg-white focus:ring-4 focus:ring-lime-200/50"
            >
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type === 'all' ? t('training.allTypes', 'All types') : t(`training.types.${type}`, type)}
                </option>
              ))}
            </select>
          </label>
        </section>

        <section className="mt-5">
          {trainingsList.length === 0 ? (
            <EmptyTrainingState onAdd={openCreate} t={t} />
          ) : filteredTrainings.length === 0 ? (
            <div className="rounded-lg border border-zinc-200 bg-white p-8 text-center">
              <CalendarDays className="mx-auto text-zinc-400" size={32} strokeWidth={1.7} />
              <h2 className="mt-3 text-xl font-black text-zinc-950">
                {t('training.noFilteredResults', 'No sessions match your filters')}
              </h2>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setTypeFilter('all');
                }}
                className="mt-4 inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 px-4 text-sm font-black text-zinc-950"
              >
                {t('training.clearFilters', 'Clear filters')}
              </button>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2" data-testid="trainings-table">
              {filteredTrainings.map((training) => (
                <TrainingCard
                  key={training.id}
                  training={training}
                  locale={locale}
                  ratedIds={ratedIds}
                  feedbackEnabled={feedbackEnabled}
                  onRate={setActiveRateTraining}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  equipmentById={equipmentById}
                  t={t}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {formMode ? (
        <TrainingForm
          mode={formMode}
          formData={formData}
          setFormData={setFormData}
          equipmentList={equipmentList}
          onSubmit={handleSubmit}
          onClose={closeForm}
          submitting={submitting}
          t={t}
        />
      ) : null}

      {feedbackEnabled && activeRateTraining && (
        <RateTrainingModal
          training={activeRateTraining}
          onClose={() => setActiveRateTraining(null)}
          onSubmitted={markRated}
        />
      )}
    </div>
  );
};

export default TrainingsPage;
